import appendQueryParams from "@/utils/appendQueryParams";
import { QueryFunctionContext } from "@tanstack/react-query";
import { PerformanceStatisticsResponse } from "@/modules/LineItem/CreateOrEdit/Display/AnalyticsForm/analytics.types";
import { PaginatedDisplayLineItemsResponse } from "@/modules/LineItems/lineItems.interface";
import client from "../client";

export function createLineItem({
  campaignId,
  payload,
}: {
  campaignId: number | string;
  payload: any;
}) {
  return client.post(`/campaigns/${campaignId}/lineItems`, payload);
}
export function createFacebookLineItem({
  payload,
}: {
  campaignId: number | string;
  payload: any;
}) {
  return client.post(`/line-item`, payload);
}
export function editFacebookLineItem({
  payload,
  lineItemId,
}: {
  lineItemId: number | string;
  payload: any;
}) {
  return client.patch(`/line-item/${lineItemId}`, payload);
}
export function editLineItem({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: any;
}) {
  return client.patch(`/lineItems/${lineItemId}`, payload);
}
export function getLineItems(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/lineItems`, data.meta?.queryParams ?? {})
  );
}
// Server-paginated, DISPLAY-only, cross-campaign line items for the top-level
// "Line Items" module. Distinct from `getLineItems` above (unpaginated,
// multi-channel) — do NOT merge them. Response is `{ rows, count }`; the axios
// response interceptor unwraps `.data`, so cast per the CLAUDE.md gotcha.
export function getPaginatedDisplayLineItems(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/lineItems/display`, data.meta?.queryParams ?? {})
  ) as unknown as Promise<PaginatedDisplayLineItemsResponse>;
}
export function getFacebookLineItem(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `/line-item/facebook/${data?.meta?.id}`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function getExternalLineItem(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/lineItems/external`, data.meta?.queryParams ?? {})
  );
}
export function getLinkedinLineItems(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`linkedin/campaigns`, data.meta?.queryParams ?? {})
  );
}
export function deleteBeeswaxLineItem(
  id: number | string
) {
  return client.delete(`/line-item/display/${id}`);
}
export function validateDisplayLineItem(
  id: number,
  payload: Record<string, any>,
  context: QueryFunctionContext
) {
  const controller = new AbortController();
  const { signal } = context;
  signal?.addEventListener("abort", () => {
    controller.abort();
  });
  try {
    return client.post(`/line-item/display/validate/${id}`, payload, {
      signal: controller.signal,
    });
  } finally {
    signal?.removeEventListener("abort", () => {
      controller.abort();
    });
  }
}

export interface UpdateCreativeAssociationsPayload {
  lineItemId: number | string;
  creatives: Array<{
    internalAdId: number;
    active: boolean;
    // Association weight (1-100) + flight dates ("YYYY-MM-DD HH:mm:ss", America/New_York).
    // null clears a previously-set value; omitted keeps the Beeswax default.
    weight?: number | null;
    startDate?: string | null;
    endDate?: string | null;
  }>;
}

export interface UpdateCreativeAssociationsResponse {
  success: boolean;
  created: Array<{ internalAdId: number; beeswaxAdAssociationId: number; active: boolean }>;
  updated: Array<{ internalAdId: number; beeswaxAdAssociationId: number; active: boolean }>;
  deleted: Array<{ internalAdId: number; beeswaxAdAssociationId: number }>;
  failed: {
    create: Array<{ internalAdId: number; error: string }>;
    update: Array<{ internalAdId: number; beeswaxAdAssociationId: number; error: string }>;
    delete: Array<{ internalAdId: number; beeswaxAdAssociationId: number; error: string }>;
  };
  lineItemDeactivated: boolean;
}

export function updateCreativeAssociations({
  lineItemId,
  creatives,
}: UpdateCreativeAssociationsPayload) {
  return client.patch<UpdateCreativeAssociationsResponse>(
    `/line-item/display/${lineItemId}/creative-associations`,
    { creatives }
  );
}

export interface BeeswaxAssociation {
  internalAdId: number | null;
  beeswaxAdAssociationId: number;
  active: boolean;
  // From Beeswax v2: weight (int 1-100, nullable) + flight dates ("YYYY-MM-DD HH:mm:ss").
  weight?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

export function getCreativeAssociations(lineItemId: number | string): Promise<BeeswaxAssociation[]> {
  return client.get(`/line-item/display/${lineItemId}/creative-associations`);
}

// ─── Bid modifier (line-item Modifiers tab) ─────────────────────────────────
// A bid modifier term is flat: one targeting_key + value + bid multiplier.
export interface BidModifierTermPayload {
  targeting_key: string;
  comparator?: "equals" | "in_range" | "boolean_expression";
  value: string;
  multiplier: string;
  override_multiplier?: boolean;
}

// Beeswax bid-modifier object (as returned by the pickable list / attached-on-load).
export interface BidModifier {
  id: number;
  name: string;
  advertiser_id: number | null;
  account_id: number;
  active: boolean;
  notes: string | null;
  bid_model_id: number | null;
  terms: BidModifierTermPayload[];
}

export interface PickableBidModifierListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BidModifier[];
}

// Create a NEW bid modifier for the line item's advertiser and attach it in one
// call (the "Create New" path). Advertiser is derived server-side from the line
// item's campaign, so no advertiser param is needed here. max_bid is required by
// Beeswax whenever a modifier is set; min_bid is optional.
export interface CreateBidModifierPayload {
  name: string;
  active?: boolean;
  notes?: string | null;
  terms: BidModifierTermPayload[];
  maxBid: string;
  minBid?: string | null;
}

export interface CreateBidModifierResponse {
  modifier: BidModifier;
  lineItem: Record<string, any>;
}

export function createAndAttachBidModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: CreateBidModifierPayload;
}) {
  return client.post<CreateBidModifierResponse>(
    `/line-item/display/${lineItemId}/bid-modifier`,
    payload
  ) as unknown as Promise<CreateBidModifierResponse>;
}

// Attach an existing bid modifier (bidModifierId = number) or detach (null).
// max_bid must be present when attaching; detach clears bid_modifier_id + both bounds.
export interface SetBidModifierPayload {
  bidModifierId: number | null;
  maxBid?: string | null;
  minBid?: string | null;
}

export function setBidModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: SetBidModifierPayload;
}) {
  return client.patch(
    `/line-item/display/${lineItemId}/bid-modifier`,
    payload
  ) as unknown as Promise<Record<string, any>>;
}

// Update an EXISTING bid modifier in place (name/notes/terms) and apply the bid
// bounds to this line item. WARNING: a bid modifier is an advertiser-level shared
// object — Beeswax applies the change to EVERY line item using it, including
// active/serving ones, with no warning, and terms are replaced wholesale. Only
// call after the user has explicitly confirmed (see ModifierSaveChoiceDialog).
export interface UpdateBidModifierPayload {
  bidModifierId: number;
  name: string;
  notes?: string | null;
  terms: BidModifierTermPayload[];
  maxBid: string;
  minBid?: string | null;
}

export function updateBidModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: UpdateBidModifierPayload;
}) {
  return client.put<CreateBidModifierResponse>(
    `/line-item/display/${lineItemId}/bid-modifier`,
    payload
  ) as unknown as Promise<CreateBidModifierResponse>;
}

// ─── Delivery modifier (line-item Modifiers tab) ────────────────────────────
// Unlike a bid modifier term (flat, one key each), a delivery modifier term is a
// GRID ROW: it carries a `targeting` array that must repeat the SAME set of keys
// on every term. "Any Value" is the key present with `value: null` — never the
// key omitted. Beeswax rejects the whole payload otherwise.
export interface DeliveryModifierTermTargeting {
  key: string;
  // null = "Any Value" (matches anything for this key).
  value: string | number | null;
  // Delivery terms only support `equals` (unlike bid terms).
  comparator?: "equals";
  // Expand a list value into one term per list entry. Set per key, and the UI
  // keeps it uniform down a column.
  expand_list?: boolean;
}

export interface DeliveryModifierTermPayload {
  targeting: DeliveryModifierTermTargeting[];
  // Must be unique AND contiguous 1..N across the term array — `1,3` is rejected.
  rank: number;
  // 0..100, sent as a string (the backend's number branch caps at 100 but its
  // string branch allows 999.99, so strings keep the client-side rule authoritative).
  weight: string;
  // 1..100, and strictly greater than this term's share of the total weight.
  budget_cap_percentage?: number | null;
}

// Beeswax delivery-modifier object, verbatim from the pickable list (the backend
// does not reshape it). `advertiser_id: null` means account-global: attachable by
// anyone, but the backend refuses to edit it in place (403).
export interface DeliveryModifier {
  id: number;
  name: string;
  advertiser_id: number | null;
  account_id: number;
  active: boolean;
  notes: string | null;
  alternative_id: string | null;
  delivery_model_id: number | null;
  fallback_weight: string;
  fallback_budget_cap_percentage: number | null;
  terms: DeliveryModifierTermPayload[];
}

export interface PickableDeliveryModifierListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DeliveryModifier[];
}

export interface CreateDeliveryModifierResponse {
  modifier: DeliveryModifier;
  lineItem: Record<string, any>;
}

// Create a NEW delivery modifier for the line item's advertiser and attach it in
// one call. Attaching requires a delivery-capable bidding strategy on the line
// item (CPM_PACED / CPC_PACED — plain CPM and CPC are rejected by Beeswax), so
// gate the UI on that before calling.
//
// `notes` / `alternativeId` are deliberately absent: we don't render them, and
// the backend's Joi rejects unknown keys, so omitting them is both required and
// the thing that preserves any value already stored on the modifier.
export interface CreateDeliveryModifierPayload {
  name: string;
  active?: boolean;
  // Required by the backend. Weight used when no term matches; "0" = don't
  // deliver on unmatched auctions.
  fallbackWeight: string;
  fallbackBudgetCapPercentage?: number | null;
  terms: DeliveryModifierTermPayload[];
}

export function createAndAttachDeliveryModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: CreateDeliveryModifierPayload;
}) {
  return client.post<CreateDeliveryModifierResponse>(
    `/line-item/display/${lineItemId}/delivery-modifier`,
    payload
  ) as unknown as Promise<CreateDeliveryModifierResponse>;
}

// Attach an existing delivery modifier (deliveryModifierId = number) or detach
// (null). This is the ENTIRE payload — delivery has no max/min-bid analogue, so
// detach is a single field, unlike the bid equivalent.
export interface SetDeliveryModifierPayload {
  deliveryModifierId: number | null;
}

export function setDeliveryModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: SetDeliveryModifierPayload;
}) {
  return client.patch(
    `/line-item/display/${lineItemId}/delivery-modifier`,
    payload
  ) as unknown as Promise<Record<string, any>>;
}

// Update an EXISTING delivery modifier in place. WARNING: it is an
// advertiser-level shared object — Beeswax applies the change to EVERY line item
// using it, active ones included, with no warning, and terms are replaced
// wholesale. Only call after explicit confirmation (ModifierSaveChoiceDialog).
//
// No `active` key: the backend's PUT schema omits it and Joi rejects unknowns.
export interface UpdateDeliveryModifierPayload {
  deliveryModifierId: number;
  name: string;
  fallbackWeight: string;
  // Explicit null clears it — one of only two fields this endpoint can null out.
  fallbackBudgetCapPercentage?: number | null;
  terms: DeliveryModifierTermPayload[];
}

export function updateDeliveryModifier({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: UpdateDeliveryModifierPayload;
}) {
  return client.put<CreateDeliveryModifierResponse>(
    `/line-item/display/${lineItemId}/delivery-modifier`,
    payload
  ) as unknown as Promise<CreateDeliveryModifierResponse>;
}

// Distinct Beeswax targeting_keys currently targeted on a saved line item.
// Feeds the "Add Term" field-picker filter (only-in-targeting UX convention).
export function getLineItemTargetingKeys(
  lineItemId: number | string
): Promise<string[]> {
  return client.get(`/line-item/display/${lineItemId}/targeting-keys`);
}

// Real-time delivery stats for the Analytics tab. FE sends only window_hours;
// page_size is fixed server-side. The response interceptor unwraps `.data`, so
// this resolves to the response body at runtime — cast accordingly.
export function getPerformanceStatistics(
  lineItemId: number | string,
  windowHours: number
): Promise<PerformanceStatisticsResponse> {
  return client.get(
    `/line-item/display/${lineItemId}/performance-statistics?window_hours=${windowHours}`
  ) as unknown as Promise<PerformanceStatisticsResponse>;
}

export interface DuplicateLineItemPayload {
  targetInternalCampaignId: number; // internal Campaigns.id; default = source campaign
  name: string;
  lineItemType: number; // Beeswax type id: 0 banner | 1 video | 2 native
  isCtvLineItem: boolean; // true only when the user picks CTV (source must be CTV)
  copyTargeting: boolean;
  copyCreatives: boolean;
}

export interface DuplicateLineItemResponse {
  success: boolean;
  payload: { id: number; [key: string]: any };
}

export function duplicateLineItem(
  sourceLineItemId: number | string,
  payload: DuplicateLineItemPayload
) {
  return client.post<DuplicateLineItemResponse>(
    `/line-item/display/${sourceLineItemId}/duplicate`,
    payload
  );
}
